/**
 * deskFilters — PURE filter / URL-codec / suggestion-match / bucket logic for
 * Manage Admission (bk-043). Zero React imports so it stays trivially unit-
 * testable and reusable by every desk surface. The desk's filter state lives
 * in the URL (B4 restore-on-return); this module is the single translator
 * between the URL and the in-memory filter object, and the single place the
 * client-side AND-composed filtering + suggestion matching + bucket→partition
 * mapping is defined.
 *
 * Filter state shape (bk-043 — bucket scheme):
 *   { bucket, rawStatus, subchip, lowDepositOnly, withDuesOnly, doctor, dept, from, to, dfrom, dto, q }
 *   - bucket          DESK_BUCKET.CURRENTLY_ADMITTED | DESK_BUCKET.DISCHARGED | null
 *                      (null only while a raw More… status is the active view)
 *   - rawStatus       one of ADMISSION_STATUS, set ONLY via the More… picker
 *                      ('' when a bucket is active)
 *   - subchip         DESK_SUBCHIP.* — narrows the Currently-admitted union
 *                      CLIENT-SIDE ('' = no narrowing)
 *   - lowDepositOnly  money cross-cut over the Currently-admitted union
 *   - withDuesOnly    money cross-cut over DUES_ELIGIBLE_STATUSES (Discharged∪Deceased)
 *   - doctor       member id of a TREATING doctor — the primary (`primary_doctor_member_id`)
 *                  OR any added doctor (`added_doctors[].member_id`) ('' = all)
 *   - dept         department_id | '__none__' (explicit "No department") | '' = all
 *   - from,to      admitted-on inclusive range, 'YYYY-MM-DD' ('' = off)
 *   - dfrom,dto    discharge-date inclusive range, 'YYYY-MM-DD' ('' = off).
 *                  Additive keys — a SEPARATE dimension from from/to (never the
 *                  same field re-interpreted); the desk's "Today" control (D5
 *                  cheap client-side slice, owner-decisions.md) is the only
 *                  writer today — both always set together to the IST "today".
 *   - q            free search text
 */

import {
  ADMISSION_STATUS,
  DESK_BUCKET,
  DESK_BUCKET_DEFAULT,
  DESK_SUBCHIP,
  CURRENTLY_ADMITTED_STATUSES,
  DISCHARGED_BUCKET_STATUSES,
  DISCHARGE_COMPLETED_STATUSES,
  bucketForStatus,
  isAwaitingBed,
  isBedAssigned,
  isInDischargeFlow,
  isNoDoctor,
  isTerminal,
  statusSince,
  duesAgingDays,
  duesAgingBucket,
  istCalendarDate,
  isMoneyHidden,
  doctorDisplayName,
  CARE_TEAM_UNNAMED_DOCTOR,
} from '../../constants/admission';

// "With dues" (copy-deck §6, R1 fix) rides EVERY dues-eligible terminal status —
// the same set `hasFinalBill` dispatches on (DISCHARGED covers NORMAL/LAMA/DAMA/
// REFERRED, DECEASED is its own status) — never DISCHARGED alone. Single source:
// reuse DISCHARGE_COMPLETED_STATUSES, don't inline a second status list.
export const DUES_ELIGIBLE_STATUSES = Array.from(DISCHARGE_COMPLETED_STATUSES);

export const NO_DEPARTMENT = '__none__';

// Desk department filter — "no department narrowing" option label. A walled
// viewer's default reads "All my departments" (stories § Copy).
export const DESK_DEPT_ALL_COPY = 'All departments';
export const DESK_DEPT_ALL_MINE_COPY = 'All my departments';

/**
 * Options of the desk Department filter (admission-access S7-AC4).
 *
 * `deptScoped` is the SERVER's word (`dept_scoped` on the list response: the
 * admission wall applied to this caller) — never re-derived from role here.
 *   - walled   → the viewer's OWN departments only (an Emergency row of another
 *                department still shows, but only under the default, since no
 *                specific department matches it);
 *   - unwalled → every department, exactly as before.
 * `viewerDeptIds` undefined (edges not hydrated yet) ⇒ no own departments, never
 * the org list. The "No department" option is appended by the caller.
 *
 * @returns {{ allLabel: string, departments: Array }}
 */
export function deskDepartmentOptions({ departments, deptScoped, viewerDeptIds }) {
  const all = Array.isArray(departments) ? departments : [];
  if (!deptScoped) return { allLabel: DESK_DEPT_ALL_COPY, departments: all };
  const own = Array.isArray(viewerDeptIds) ? viewerDeptIds : [];
  return {
    allLabel: DESK_DEPT_ALL_MINE_COPY,
    departments: all.filter((d) => own.includes(d.dept_id || d.id)),
  };
}

export function defaultDeskFilters() {
  return {
    bucket: DESK_BUCKET_DEFAULT,
    rawStatus: '',
    subchip: '',
    lowDepositOnly: false,
    withDuesOnly: false,
    doctor: '',
    dept: '',
    from: '',
    to: '',
    dfrom: '',
    dto: '',
    q: '',
    // Owner ruling 2026-08-12: the rail defaults to IP-number order
    // (low→high) — replaces the old ward|bed default, which read "jumbled"
    // to front-desk staff. 'ward' is kept as an explicit opt-in (see
    // `sortDeskRows`).
    sortBy: 'ip_asc',
  };
}

/** URLSearchParams (or any {get}) → filter state, defaults for absent/garbage params. */
export function parseDeskParams(searchParams) {
  const get = (k) => (searchParams && typeof searchParams.get === 'function' ? searchParams.get(k) : null);
  const filter = get('filter');
  const awaitingOnly = filter === 'awaiting_bed';
  const bedAssignedOnly = filter === 'bed_assigned';
  const dischargeInProgressOnly = filter === 'discharge_in_progress';
  const noDoctorOnly = filter === 'no_doctor';
  const lowDepositOnly = filter === 'low_deposit';
  // "With dues" (copy-deck §6, R1 fix) spans EVERY dues-eligible terminal status
  // (DISCHARGED + DECEASED — DUES_ELIGIBLE_STATUSES) — the container union-loads
  // both partitions, never DISCHARGED alone (a death-with-dues row must appear).
  const withDuesOnly = filter === 'with_dues';
  const hasSubchip = awaitingOnly || bedAssignedOnly || dischargeInProgressOnly || noDoctorOnly;
  const rawStatusParam = get('status');
  const validRawStatus = rawStatusParam && ADMISSION_STATUS[rawStatusParam] ? rawStatusParam : '';
  const bucketParam = get('bucket');

  let bucket;
  let rawStatus = '';
  if (hasSubchip || lowDepositOnly) {
    bucket = DESK_BUCKET.CURRENTLY_ADMITTED;
  } else if (withDuesOnly) {
    bucket = DESK_BUCKET.DISCHARGED;
  } else if (validRawStatus) {
    bucket = null;
    rawStatus = validRawStatus;
  } else if (bucketParam === 'discharged') {
    bucket = DESK_BUCKET.DISCHARGED;
  } else {
    // Default (no params) AND any unrecognised/garbage bucket|filter|status
    // value falls back here — never a blank/broken view (S4 AC5).
    bucket = DESK_BUCKET_DEFAULT;
  }

  const subchip = awaitingOnly
    ? DESK_SUBCHIP.AWAITING_BED
    : bedAssignedOnly
      ? DESK_SUBCHIP.BED_ASSIGNED
      : dischargeInProgressOnly
        ? DESK_SUBCHIP.DISCHARGE_IN_PROGRESS
        : noDoctorOnly
          ? DESK_SUBCHIP.NO_DOCTOR
          : '';

  return {
    bucket,
    rawStatus,
    subchip,
    lowDepositOnly,
    withDuesOnly,
    doctor: get('doctor') || '',
    dept: get('dept') || '',
    from: get('from') || '',
    to: get('to') || '',
    // Additive discharge-date keys (D5 cheap slice) — same no-validation codec
    // convention as from/to: a garbage value just fails to match any row
    // (empty result, never a crash/blank screen), never repurposes from/to.
    dfrom: get('dfrom') || '',
    dto: get('dto') || '',
    q: get('q') || '',
  };
}

/**
 * withoutMoneyFilters — admission-access (MOCK-4): a viewer who cannot hold the
 * money filters (no VIEW_RUNNING_BILL + VIEW_IPD_DASHBOARD — every doctor, the
 * nurse) must not reach them by a `?filter=with_dues|low_deposit` deep link
 * either: both flags are cleared, the rest of the state (bucket, doctor,
 * dept, q …) is untouched. Same reference back when nothing needs clearing.
 */
export function withoutMoneyFilters(filterState) {
  const f = filterState || {};
  if (!f.lowDepositOnly && !f.withDuesOnly) return f;
  return { ...f, lowDepositOnly: false, withDuesOnly: false };
}

/** Filter state → URLSearchParams; only non-default params are emitted (clean URLs). */
export function serializeDeskParams(filterState) {
  const f = { ...defaultDeskFilters(), ...(filterState || {}) };
  const params = new URLSearchParams();
  // Sub-chips / money chips / raw status / non-default bucket are mutually
  // exclusive deep-link tokens — never emit more than one `filter`/`status`/
  // `bucket` param at a time.
  if (f.subchip === DESK_SUBCHIP.AWAITING_BED) {
    params.set('filter', 'awaiting_bed');
  } else if (f.subchip === DESK_SUBCHIP.BED_ASSIGNED) {
    params.set('filter', 'bed_assigned');
  } else if (f.subchip === DESK_SUBCHIP.DISCHARGE_IN_PROGRESS) {
    params.set('filter', 'discharge_in_progress');
  } else if (f.subchip === DESK_SUBCHIP.NO_DOCTOR) {
    params.set('filter', 'no_doctor');
  } else if (f.lowDepositOnly) {
    params.set('filter', 'low_deposit');
  } else if (f.withDuesOnly) {
    params.set('filter', 'with_dues');
  } else if (f.rawStatus) {
    params.set('status', f.rawStatus);
  } else if (f.bucket === DESK_BUCKET.DISCHARGED) {
    params.set('bucket', 'discharged');
  }
  // f.bucket === DESK_BUCKET_DEFAULT with nothing else active → no params.
  if (f.doctor) params.set('doctor', f.doctor);
  if (f.dept) params.set('dept', f.dept);
  if (f.from) params.set('from', f.from);
  if (f.to) params.set('to', f.to);
  if (f.dfrom) params.set('dfrom', f.dfrom);
  if (f.dto) params.set('dto', f.dto);
  if (f.q) params.set('q', f.q);
  return params;
}

/**
 * bucketPartitions — the raw status partitions a filter state's ACTIVE view
 * needs loaded, for the container's ONE bounded `Promise.all` (S3 AC1). This
 * is the single mapping every bucket/money-chip/raw-status selection reads —
 * never a second inline status list.
 *   - a raw More… status  → that ONE partition
 *   - "With dues"          → DUES_ELIGIBLE_STATUSES (2: Discharged, Deceased)
 *   - Discharged bucket    → DISCHARGED_BUCKET_STATUSES (3)
 *   - Currently-admitted / sub-chip / Low deposit → CURRENTLY_ADMITTED_STATUSES (4)
 */
export function bucketPartitions(filterState) {
  const f = { ...defaultDeskFilters(), ...(filterState || {}) };
  if (f.rawStatus) return [f.rawStatus];
  if (f.withDuesOnly) return DUES_ELIGIBLE_STATUSES;
  if (f.bucket === DESK_BUCKET.DISCHARGED) return DISCHARGED_BUCKET_STATUSES;
  return CURRENTLY_ADMITTED_STATUSES;
}

/**
 * isDischargeWindowView (discharge-perf F2) — true exactly when the active
 * view is the plain Discharged bucket (same precedence as `bucketPartitions`:
 * a raw More… status or "With dues" wins). That view loads via ONE
 * `listDischarges` window call instead of the per-status partitions.
 */
export function isDischargeWindowView(filterState) {
  const f = filterState || {};
  return !f.rawStatus && !f.withDuesOnly && f.bucket === DESK_BUCKET.DISCHARGED;
}

/**
 * withoutStaleDischargeWindow (bk-1784) — a view switch to anything but the
 * Discharged window view must drop dfrom/dto, or they hide every row that
 * has no discharge date. Single point for every pick/highlight patch builder.
 */
export function withoutStaleDischargeWindow(patch) {
  return isDischargeWindowView(patch) ? patch : { ...patch, dfrom: '', dto: '' };
}

/**
 * discharge-perf F3 — pure date-range helpers for the Discharged desk's
 * presets (Today / Last 30 days / custom) and D1's 3-month custom-span cap.
 * Calendar-date arithmetic on an ALREADY-RESOLVED 'YYYY-MM-DD' IST key uses
 * `Date.UTC` purely as a neutral integer clock for +/-N-day math — this is
 * NOT the `toISOString().slice(0,10)` anti-pattern (which derives "today"
 * from a live UTC instant and mis-buckets near the IST midnight boundary);
 * the day-boundary-correct value is `istCalendarDate` above, computed once,
 * then shifted here as a plain calendar key.
 */
function dateKeyToUTCms(key) {
  const [y, m, d] = key.split('-').map(Number);
  return Date.UTC(y, m - 1, d);
}

function shiftDateKey(key, days) {
  const dt = new Date(dateKeyToUTCms(key));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

function daysBetweenKeys(fromKey, toKey) {
  return Math.round((dateKeyToUTCms(toKey) - dateKeyToUTCms(fromKey)) / 86400000);
}

/** D1 (owner-decisions, LOCKED): a custom Discharged range spans at most 3
 * months — 92 calendar days inclusive. Single source for the FE guard (F3)
 * and the F4 search-pick widen cap; the server enforces the same number
 * independently (contract §1 `span` rule) — never trust the client alone. */
export const DISCHARGE_WINDOW_MAX_SPAN_DAYS = 92;

const LAST_30_DAYS_SPAN = 30;

/**
 * D4 (owner-decisions, LOCKED): the Discharged desk's default window — the
 * SAME rule the server resolves blank `from`/`to` to (contract §1: "both
 * omitted ⇒ from = IST today − 29 d, to = IST today"). Computed here so the
 * "Last 30 days" preset's highlighted state can be derived WITHOUT waiting
 * on the server's echoed `period` (single formula, never a second one that
 * could drift from the backend's).
 */
export function defaultDischargeWindowIST() {
  const to = istCalendarDate(new Date());
  return { from: shiftDateKey(to, -(LAST_30_DAYS_SPAN - 1)), to };
}

/**
 * dischargeWindowPreset — which preset (if any) the current `dfrom`/`dto`
 * resolve to: 'today' | 'last30' | 'custom'. Blank ALWAYS reads as 'last30'
 * (the server default); an explicit pair that happens to equal today's date
 * or the last-30-days window also reads as that preset (e.g. a bookmarked
 * URL) — single source for the DeskFilterBar highlight AND any future
 * consumer, never a second inline comparison.
 */
export function dischargeWindowPreset(filterState) {
  const f = filterState || {};
  if (!f.dfrom && !f.dto) return 'last30';
  const today = istCalendarDate(new Date());
  if (f.dfrom === today && f.dto === today) return 'today';
  const last30 = defaultDischargeWindowIST();
  if (f.dfrom === last30.from && f.dto === last30.to) return 'last30';
  return 'custom';
}

// Fixed 3-letter month abbreviations — NEVER `toLocaleDateString(...,
// {month:'short'})`: `en-IN` renders September as "Sept" (4 letters), the
// exact bug already logged against `ManageVisitPage.print` (bk-1406/bk-1497).
// `period.from`/`.to` are already resolved 'YYYY-MM-DD' IST calendar keys
// (contract §1), so this is plain string parsing — no Date/timezone needed.
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** 'YYYY-MM-DD' IST calendar key -> 'D Mon' or 'D Mon YYYY' (e.g. '1 Jul', '1 Jul 2026'). */
function formatShortDayMonthIST(key, withYear) {
  const [y, m, d] = key.split('-');
  return `${Number(d)} ${MONTH_ABBR[Number(m) - 1]}${withYear ? ` ${y}` : ''}`;
}

/**
 * dischargeWindowLabel (QA-FE-3, QA-FE-3b fix) — the ONE label built from the
 * SERVER-echoed `period`, for both the count pill ("N discharged · {label}")
 * and the window empty-state copy. Reuses `dischargeWindowPreset` (never a
 * second today/last-30 comparison) so the pill/empty-state can never
 * disagree with the preset-bar highlight. QA-owned copy note: "today" /
 * "last 30 days" / a short range are PO/Business to ratify at click-through —
 * placeholder wording only.
 *
 * QA-FE-3b: a custom range ALWAYS carries the year (mockup: "1 Jun – 30 Jun
 * 2026") — never a bare "D Mon – D Mon" that silently reads as "this year".
 * A single-day range collapses to one "D Mon YYYY" (never "D Mon – D Mon").
 * When the two ends fall in different years, both sides get a year
 * ("15 Dec 2025 – 10 Jan 2026") so neither end is ambiguous on its own.
 */
export function dischargeWindowLabel(period) {
  if (!period || !period.from || !period.to) return '';
  const preset = dischargeWindowPreset({ dfrom: period.from, dto: period.to });
  if (preset === 'today') return 'today';
  if (preset === 'last30') return 'last 30 days';
  if (period.from === period.to) return formatShortDayMonthIST(period.to, true);
  const sameYear = period.from.slice(0, 4) === period.to.slice(0, 4);
  return `${formatShortDayMonthIST(period.from, !sameYear)} – ${formatShortDayMonthIST(period.to, true)}`;
}

/**
 * dischargeWindowPhrase (pre-PR review SHOULD-FIX) — the ONE period->words
 * mapping used in PROSE ("today" / "in the last 30 days" / "on D Mon YYYY" /
 * "between D Mon [YYYY] and D Mon YYYY"), built straight from `period.from`/
 * `.to` with the same `formatShortDayMonthIST` formatter and `MONTH_ABBR`
 * table `dischargeWindowLabel` uses for the pill — NOT by rewriting the
 * pill's dash-joined label string. Both the window empty-state
 * (`AdmissionDeskPage`'s `dischargeEmptyPhrase`) and `dischargeSearchEmptyHint`
 * below read this ONE mapping — never a second one. `''` when there is no
 * resolvable period; callers supply their own fallback wording.
 */
export function dischargeWindowPhrase(period) {
  if (!period || !period.from || !period.to) return '';
  const preset = dischargeWindowPreset({ dfrom: period.from, dto: period.to });
  if (preset === 'today') return 'today';
  if (preset === 'last30') return 'in the last 30 days';
  if (period.from === period.to) return `on ${formatShortDayMonthIST(period.to, true)}`;
  const sameYear = period.from.slice(0, 4) === period.to.slice(0, 4);
  return `between ${formatShortDayMonthIST(period.from, !sameYear)} and ${formatShortDayMonthIST(period.to, true)}`;
}

/**
 * dischargeSearchEmptyHint (R1, business verdict 2026-09-30) — the Discharged
 * window view narrowed its name search from ~500 rows (all-time index) to
 * the active `dfrom..dto` window (bk-512's user-facing symptom). A search
 * that finds nothing must say WHICH window it searched and how to widen it —
 * a bare "No matches." reads as "this record is gone". Built from the ONE
 * shared `dischargeWindowPhrase` (never a second period->words mapping), so
 * this can never disagree with the empty-state phrase.
 * `null` when there is no period (not the Discharged window view) — the
 * caller falls back to the plain "No matches." there, unchanged.
 * PO/Business ratified 2026-09-30.
 */
export function dischargeSearchEmptyHint(period) {
  const phrase = dischargeWindowPhrase(period);
  if (!phrase) return null;
  if (phrase === 'today') return 'No matches for today. Use Custom dates to search older discharges.';
  if (phrase === 'in the last 30 days') return 'No matches in the last 30 days. Use Custom dates to search older discharges.';
  // phrase is 'on D Mon YYYY' or 'between D Mon [YYYY] and D Mon YYYY' —
  // both already read correctly straight after "No matches ".
  return `No matches ${phrase}. Use Custom dates to search other dates.`;
}

/** PO-owned copy (final wording pending); D1's guard needs SOME inline
 * message today, kept as one named constant so a copy change is a one-line
 * edit, never a re-typed literal at each call site. */
export const DISCHARGE_RANGE_ERROR = {
  INVERTED: 'Pick an end date on or after the start date.',
  SPAN: 'Please pick up to 3 months.',
  MISSING: 'Pick both dates.',
};

/**
 * validateDischargeRange — the FE's own copy of the D1 guard (contract §1's
 * `inverted`/`span` 422 reasons), so the picker blocks BEFORE a round trip.
 * A future `to` is explicitly allowed (owner-decisions §1 "to in the future
 * ⇒ 200"); only inverted and over-span are rejected.
 *
 * QA-FE-1 (fixed): exactly one blank side (or both blank) is INVALID — the
 * server's own `one_of` 422 reason for a half-open range — so Apply must
 * never fire a half-window request. The caller (DeskFilterBar) never even
 * reaches this with the seeded default both-blank case in practice (the
 * custom editor always seeds both drafts from the committed window or the
 * last-30-days default before the user can touch Apply), but treating a
 * blank pair as invalid too keeps this ONE rule for every blank/partial
 * combination — never a second "both blank is fine" branch.
 */
export function validateDischargeRange(dfrom, dto) {
  if (!dfrom || !dto) return { valid: false, message: DISCHARGE_RANGE_ERROR.MISSING };
  if (dfrom > dto) return { valid: false, message: DISCHARGE_RANGE_ERROR.INVERTED };
  const spanDays = daysBetweenKeys(dfrom, dto) + 1; // inclusive
  if (spanDays > DISCHARGE_WINDOW_MAX_SPAN_DAYS) return { valid: false, message: DISCHARGE_RANGE_ERROR.SPAN };
  return { valid: true, message: null };
}

/**
 * viewForStatus — the bucket-or-raw filter-state patch that makes a given
 * admission's raw status visible, clearing every other narrowing filter.
 * Single source for post-admit highlight-nav + cross-status suggestion pick
 * (S2 "post-admit highlight" + S5 AC2) — both land on the SAME view a status
 * resolves to, via the S1 `bucketForStatus` constant (never a second mapping).
 */
export function viewForStatus(status) {
  const bucket = bucketForStatus(status);
  if (bucket) {
    return { bucket, rawStatus: '', subchip: '', lowDepositOnly: false, withDuesOnly: false };
  }
  // Unbucketed (dead-ladder / cancelled) — only reachable via the raw view.
  return { bucket: null, rawStatus: status, subchip: '', lowDepositOnly: false, withDuesOnly: false };
}

/** 'YYYY-MM-DD' local date key for an ISO admitted_at timestamp (null-safe). */
function admittedDateKey(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * 'YYYY-MM-DD' IST calendar-date key for a row's `discharged_at` (null-safe).
 * Reuses the ONE shared IST helper (`istCalendarDate`, constants/admission.js
 * — the same one `duesAgingDays` reads) rather than hand-rolling a second date
 * helper; deliberately does NOT reuse `admittedDateKey`'s browser-local logic
 * (that is a known bug class — IST midnight–05:30 mis-buckets in the browser's
 * own timezone, left untouched here per task scope).
 */
function dischargeDateKey(iso) {
  return istCalendarDate(iso);
}

function matchesQuery(row, q) {
  if (!q) return true;
  const name = (row.patient_name || '').toLowerCase();
  const ip = (row.ip_number || '').toLowerCase();
  // IPD picker (diag C6a) — bed is additionally searchable. Purely additive
  // (an OR clause that can only ADD matches), so every existing name/IP
  // search caller is unaffected.
  const bed = (row.bed_label || '').toLowerCase();
  return name.includes(q) || ip.includes(q) || bed.includes(q);
}

/**
 * isTreatedBy (admission-access M-FE-2, S17-AC2) — is `memberId` a treating
 * doctor on this admission: the primary OR any added doctor. The ONE predicate
 * behind the desk's doctor filter; a missing `added_doctors` (older server) is
 * an empty list, so the filter degrades to today's primary-only match.
 */
export function isTreatedBy(row, memberId) {
  if (!memberId) return false;
  if (row.primary_doctor_member_id === memberId) return true;
  const added = Array.isArray(row.added_doctors) ? row.added_doctors : [];
  return added.some((d) => d.member_id === memberId);
}

/**
 * collectRowDoctors — every doctor (primary or added; the SAME fields
 * `isTreatedBy` reads) on the loaded rows, deduped by member id, with the
 * department ids of the rows they appear on. Names: the org roster first, else
 * the row's own name field. Feeds the walled desk's doctor dropdown so a
 * care-team doctor from another department on a VISIBLE row is selectable.
 */
export function collectRowDoctors(rows, roster = []) {
  const rosterName = new Map((roster || []).map((d) => [d.member_id, d.name]));
  const byId = new Map();
  const add = (id, rowName, deptId) => {
    if (!id) return;
    let e = byId.get(id);
    if (!e) {
      const name = rosterName.get(id) || String(rowName || '').replace(/^dr\.?\s+/i, '').trim();
      e = { member_id: id, doctor_id: id, name, deptIds: new Set() };
      byId.set(id, e);
    }
    e.deptIds.add(deptId == null ? null : deptId);
  };
  (rows || []).forEach((r) => {
    add(r.primary_doctor_member_id, r.primary_doctor_name, r.department_id);
    (Array.isArray(r.added_doctors) ? r.added_doctors : []).forEach((d) => add(d.member_id, d.name, r.department_id));
  });
  return Array.from(byId.values());
}

// First N treating doctors are named, the rest collapse to "+n" (S16-AC7).
export const TREATING_NAMES_SHOWN = 3;

/**
 * treatingDoctorLabel — the doctor line on the rail, the workspace header and
 * the IPD page row: the primary first, then the added doctors, the first three
 * named and the remainder as "+n". With no added doctors it is exactly today's
 * `primary_doctor_name || 'No doctor'`. Single source for all three surfaces.
 */
export function treatingDoctorLabel(row) {
  const added = Array.isArray(row.added_doctors) ? row.added_doctors : [];
  const names = [
    row.primary_doctor_name || 'No doctor',
    ...added.map((d) => doctorDisplayName(d.name) || CARE_TEAM_UNNAMED_DOCTOR), // #47
  ];
  const label = names.slice(0, TREATING_NAMES_SHOWN).join(', ');
  return names.length > TREATING_NAMES_SHOWN ? `${label} +${names.length - TREATING_NAMES_SHOWN}` : label;
}

/**
 * AND-compose the secondary filters over the already-bucket/status-scoped
 * rows. The bucket/rawStatus selection itself is NOT re-filtered here (the
 * container loads exactly the right partition union); `subchip` is the
 * client-side narrowing exception over the Currently-admitted union.
 *
 * Empty-set rule (memory: unassigned = org-wide): a row with department_id == null
 * passes the "All departments" view AND the explicit "No department" (__none__)
 * option, and is excluded ONLY when a specific department is selected.
 */
export function applyDeskFilters(rows, filterState) {
  const f = { ...defaultDeskFilters(), ...(filterState || {}) };
  const q = (f.q || '').trim().toLowerCase();
  const list = Array.isArray(rows) ? rows : [];
  return list.filter((r) => {
    if (f.subchip === DESK_SUBCHIP.AWAITING_BED && !isAwaitingBed(r)) return false;
    if (f.subchip === DESK_SUBCHIP.BED_ASSIGNED && !isBedAssigned(r)) return false;
    if (f.subchip === DESK_SUBCHIP.DISCHARGE_IN_PROGRESS && !isInDischargeFlow(r.status)) return false;
    if (f.subchip === DESK_SUBCHIP.NO_DOCTOR && !isNoDoctor(r)) return false;
    // "Low deposit" reads the SERVER overlay flag only — never a client-side
    // balance rule (single source; the flag rides the admission row denorm).
    // Owner decision (Q3, 2026-07-15): spans the FULL Currently-admitted union.
    if (f.lowDepositOnly && !r.deposit_low) return false;
    // "With dues" reads the SERVER-stamped dues_paise ONLY (single source — the
    // same field the desk chip and the final-bill footer read; build-plan QA#10).
    if (f.withDuesOnly && !(Number(r.dues_paise) > 0)) return false;
    if (f.doctor && !isTreatedBy(r, f.doctor)) return false;
    if (f.dept) {
      if (f.dept === NO_DEPARTMENT) {
        if (r.department_id != null) return false;
      } else if (r.department_id !== f.dept) {
        return false;
      }
    }
    // Admitted-on range (from/to) — HIDDEN + ignored on the Discharged window
    // view (owner click-through 2026-09-30): the control clutters the preset
    // row next to Today/Last 30 days/Custom, and a hidden filter must never
    // silently narrow the list. `from`/`to` are NOT cleared here (bookmarked
    // URL keys) — they apply again the moment the user switches to another
    // bucket (Currently admitted, With dues, More…).
    if (!isDischargeWindowView(f) && (f.from || f.to)) {
      const key = admittedDateKey(r.admitted_at);
      if (!key) return false; // no admitted date → cannot fall in an admitted-on range
      if (f.from && key < f.from) return false;
      if (f.to && key > f.to) return false;
    }
    // Discharge-date range (additive dfrom/dto) — reads `discharged_at` ONLY;
    // a row missing it can never satisfy a discharge-date filter (never falls
    // back to admitted_at/statusSince — a wrong date is worse than an
    // excluded row, task requirement 3 / bk-510).
    // discharge-perf F3 — RETIRED for the Discharged window view: `rows` are
    // already the server's exact `dfrom..dto` window (contract §1), so
    // re-filtering client-side here can only ever be a no-op at best or,
    // worse, drop a row the server correctly included on an edge case the
    // client's own date-key computation disagrees with. Every other view
    // never sets dfrom/dto in the first place, so this only changes behaviour
    // for the one view it was written for.
    if (!isDischargeWindowView(f) && (f.dfrom || f.dto)) {
      const key = dischargeDateKey(r.discharged_at);
      if (!key) return false;
      if (f.dfrom && key < f.dfrom) return false;
      if (f.dto && key > f.dto) return false;
    }
    return matchesQuery(r, q);
  });
}

/** Stable tiebreak for equal/absent IP numbers: admitted_at, then admission id. */
function compareIpTiebreak(a, b) {
  const ta = a.admitted_at || '';
  const tb = b.admitted_at || '';
  if (ta !== tb) return ta.localeCompare(tb);
  return String(a.admission_id || '').localeCompare(String(b.admission_id || ''));
}

/**
 * compareByIp — numeric-aware IP-number compare (`/0100` sorts after `/0099`
 * regardless of zero-padding or FY prefix, via Intl numeric collation).
 * Rows with a null/absent `ip_number` sort LAST in BOTH directions — 'desc'
 * only reverses the real IP comparison, never the null placement.
 */
function compareByIp(a, b, direction) {
  const ipA = a.ip_number;
  const ipB = b.ip_number;
  const aBlank = ipA == null || ipA === '';
  const bBlank = ipB == null || ipB === '';
  if (aBlank && bBlank) return compareIpTiebreak(a, b);
  if (aBlank) return 1;
  if (bBlank) return -1;
  const cmp = String(ipA).localeCompare(String(ipB), undefined, { numeric: true, sensitivity: 'base' });
  if (cmp !== 0) return direction === 'desc' ? -cmp : cmp;
  return compareIpTiebreak(a, b);
}

/**
 * sortDeskRows — S2 AC7 baseline, extended for the IP-number default (owner
 * ruling 2026-08-12, DESK-IP-SORT): `sortBy` drives every bucket/view —
 *   - 'ip_asc' | 'ip_desc' (DEFAULT: 'ip_asc') → numeric-aware IP-number
 *     order, null ip_number rows LAST regardless of direction. Applies to
 *     Currently admitted, Discharged, AND a raw More… single-status view
 *     (a raw-status list is exactly where jumbling showed up).
 *   - 'ward' → the legacy comparators, preserved as an explicit opt-in:
 *     Currently admitted → ward/bed order (nurse-station walk); Discharged →
 *     newest-discharge-first; raw More… view → the server's existing
 *     (unsorted) order, unchanged (S2 AC4).
 */
export function sortDeskRows(rows, filterState) {
  const f = { ...defaultDeskFilters(), ...(filterState || {}) };
  const list = Array.isArray(rows) ? [...rows] : [];
  const ipDirection = f.sortBy === 'ip_desc' ? 'desc' : (f.sortBy === 'ip_asc' ? 'asc' : null);

  if (f.rawStatus) {
    if (ipDirection) return list.sort((a, b) => compareByIp(a, b, ipDirection));
    return list; // 'ward' → legacy: server's existing order, unchanged
  }
  if (f.bucket === DESK_BUCKET.DISCHARGED) {
    if (ipDirection) return list.sort((a, b) => compareByIp(a, b, ipDirection));
    return list.sort((a, b) => { // 'ward' → legacy: newest-discharge-first
      const ta = statusSince(a) || a.admitted_at || '';
      const tb = statusSince(b) || b.admitted_at || '';
      return tb.localeCompare(ta);
    });
  }
  if (ipDirection) return list.sort((a, b) => compareByIp(a, b, ipDirection));
  return list.sort((a, b) => { // 'ward' → legacy: ward/bed order
    const ka = `${a.ward_name || ''}|${a.bed_label || ''}`;
    const kb = `${b.ward_name || ''}|${b.bed_label || ''}`;
    return ka.localeCompare(kb);
  });
}

/** True iff any filter differs from the default (drives the "Clear filters" affordance). */
export function hasActiveFilters(filterState) {
  const d = defaultDeskFilters();
  const f = { ...d, ...(filterState || {}) };
  return f.bucket !== d.bucket
    || f.rawStatus !== d.rawStatus
    || f.subchip !== d.subchip
    || f.lowDepositOnly !== d.lowDepositOnly
    || f.withDuesOnly !== d.withDuesOnly
    || !!f.doctor
    || !!f.dept
    || !!f.from
    || !!f.to
    || !!f.dfrom
    || !!f.dto
    || !!(f.q && f.q.trim());
}

/**
 * widenDischargeWindow (F4) — when a search pick's discharge date falls
 * outside the CURRENT window (blank reads as the last-30-days default, same
 * rule as `dischargeWindowPreset`), widen the window to include it, capped
 * at `DISCHARGE_WINDOW_MAX_SPAN_DAYS` (D1) — the row that triggered the
 * widen must stay inside, so the cap clamps the FAR edge inward, never the
 * edge nearest the picked row. A pick already inside the current window (or
 * with no parseable discharge date) is a no-op — the committed dfrom/dto
 * (possibly blank) pass through unchanged.
 */
export function widenDischargeWindow(filterState, admission) {
  const f = filterState || {};
  const committed = { dfrom: f.dfrom || '', dto: f.dto || '' };
  const targetKey = istCalendarDate(admission && admission.discharged_at);
  if (!targetKey) return committed;
  const current = (f.dfrom && f.dto) ? { from: f.dfrom, to: f.dto } : defaultDischargeWindowIST();
  if (targetKey >= current.from && targetKey <= current.to) return committed;

  let from = targetKey < current.from ? targetKey : current.from;
  let to = targetKey > current.to ? targetKey : current.to;
  if (daysBetweenKeys(from, to) + 1 > DISCHARGE_WINDOW_MAX_SPAN_DAYS) {
    if (targetKey < current.from) {
      to = shiftDateKey(from, DISCHARGE_WINDOW_MAX_SPAN_DAYS - 1);
    } else {
      from = shiftDateKey(to, -(DISCHARGE_WINDOW_MAX_SPAN_DAYS - 1));
    }
  }
  return { dfrom: from, dto: to };
}

/**
 * anchorDischargeWindow — search pick of a discharge that is OLDER than the
 * D1 cap can reach from the current window (widening would exceed the span): re-anchor the window so it ENDS
 * 7 days after the picked discharge date (never past today) and spans the
 * last-30-days width. A nearer pick keeps `widenDischargeWindow`'s behaviour
 * (the picked row stays in; inside the window = no-op). No parseable discharge
 * date → committed dfrom/dto unchanged (the desk opens the detail pane directly).
 */
export function anchorDischargeWindow(filterState, admission) {
  const f = filterState || {};
  const targetKey = istCalendarDate(admission && admission.discharged_at);
  if (!targetKey) return { dfrom: f.dfrom || '', dto: f.dto || '' };
  const current = (f.dfrom && f.dto) ? { from: f.dfrom, to: f.dto } : defaultDischargeWindowIST();
  const reach = { from: targetKey < current.from ? targetKey : current.from, to: targetKey > current.to ? targetKey : current.to };
  if (daysBetweenKeys(reach.from, reach.to) + 1 <= DISCHARGE_WINDOW_MAX_SPAN_DAYS) return widenDischargeWindow(f, admission);
  const today = defaultDischargeWindowIST().to;
  const plus7 = shiftDateKey(targetKey, 7);
  const to = plus7 < today ? plus7 : today;
  return { dfrom: shiftDateKey(to, -(LAST_30_DAYS_SPAN - 1)), dto: to };
}

/**
 * filtersHidingPick — which of the Department / Doctor desk filters would hide
 * the picked admission from the rail. Returns { patch, cleared }: `patch`
 * blanks exactly those filters, `cleared` names them for the notice. A server
 * search row carries no doctor list, so an active doctor filter is relaxed for
 * it (the open detail pane must never be the thing a filter silently blocks).
 */
export function filtersHidingPick(filterState, row) {
  const f = filterState || {};
  const patch = {};
  const cleared = [];
  if (f.dept) {
    const hidden = f.dept === NO_DEPARTMENT ? row.department_id != null : row.department_id !== f.dept;
    if (hidden) { patch.dept = ''; cleared.push('department'); }
  }
  if (f.doctor && !isTreatedBy(row, f.doctor)) { patch.doctor = ''; cleared.push('doctor'); }
  return { patch, cleared };
}

// Live fields a server search row overlays onto a stale local/index copy.
const LIVE_SEARCH_FIELDS = ['status', 'bed_label', 'discharged_at', 'department_id', 'department_name'];

/**
 * mergeSearchResults — dropdown rows: local (rail + index) matches render
 * instantly, server rows not already shown merge in; deduped by admission_id
 * (local copy wins ordering/identity, but the live server row's status/bed/discharge/
 * department fields overlay it — the index copy can be stale). Sorted like the server (F4 / API contract): non-terminal
 * first, then created_at desc — stable, so equal keys keep local-then-server
 * order. Returns { items, more } like matchSuggestions.
 */
export function mergeSearchResults(local, serverRows, cap = 8) {
  const server = Array.isArray(serverRows) ? serverRows : [];
  const live = new Map(server.map((r) => [r.admission_id, r]));
  const items = (Array.isArray(local) ? local : []).map((r) => {
    const s = live.get(r.admission_id);
    if (!s) return r;
    const o = { ...r };
    LIVE_SEARCH_FIELDS.forEach((k) => { if (k in s) o[k] = s[k]; });
    return o;
  });
  const seen = new Set(items.map((r) => r.admission_id));
  const extra = server.filter((r) => !seen.has(r.admission_id));
  const all = [...items, ...extra].sort((a, b) => (
    (isTerminal(a.status) - isTerminal(b.status))
    || String(b.created_at || '').localeCompare(String(a.created_at || ''))
  ));
  return { items: all.slice(0, cap), more: Math.max(0, all.length - cap) };
}

/**
 * computeDeskCounts — ONE shared derivation for every count badge (S2 AC6):
 * the two bucket chips + four sub-chips. Reads ONLY rows already in memory —
 * the ACTIVE bucket's just-loaded `rows`, plus the cross-status search index
 * (`indexRows`, S5) IF it has already been warmed by a search focus — NEVER
 * triggers a fetch itself, so counting can't grow the request count (S3).
 * A bucket/sub-chip the desk cannot yet count (index not warmed, other bucket
 * inactive) reports `null` — the bar renders it with NO badge rather than a
 * misleading 0 (dead-ladder-badge lesson, S2 AC6 "no badges on More…").
 */
export function computeDeskCounts(rows, indexRows, filterState) {
  const f = { ...defaultDeskFilters(), ...(filterState || {}) };
  const list = Array.isArray(rows) ? rows : [];
  const idx = Array.isArray(indexRows) ? indexRows : [];
  const haveIndex = idx.length > 0;

  const activeIsCurrentlyAdmitted = f.bucket === DESK_BUCKET.CURRENTLY_ADMITTED && !f.rawStatus;
  const activeIsDischarged = f.bucket === DESK_BUCKET.DISCHARGED && !f.rawStatus;

  const currentlyAdmittedRows = activeIsCurrentlyAdmitted
    ? list
    : haveIndex
      ? idx.filter((r) => CURRENTLY_ADMITTED_STATUSES.includes(r.status))
      : null;
  // discharge-perf F4 — the Discharged badge is NEVER estimated from the
  // cross-status search index while the bucket is inactive: that index is an
  // unbounded/all-time read, and reusing it here would silently reintroduce
  // the exact full-history cost F2 exists to remove. `null` (no badge) until
  // the window is actually the active view — never a stale/misleading count.
  const dischargedRows = activeIsDischarged ? list : null;

  return {
    currentlyAdmitted: currentlyAdmittedRows ? currentlyAdmittedRows.length : null,
    discharged: dischargedRows ? dischargedRows.length : null,
    awaitingBed: currentlyAdmittedRows ? currentlyAdmittedRows.filter(isAwaitingBed).length : null,
    bedAssigned: currentlyAdmittedRows ? currentlyAdmittedRows.filter(isBedAssigned).length : null,
    dischargeInProgress: currentlyAdmittedRows
      ? currentlyAdmittedRows.filter((r) => isInDischargeFlow(r.status)).length
      : null,
    noDoctor: currentlyAdmittedRows ? currentlyAdmittedRows.filter(isNoDoctor).length : null,
  };
}

/**
 * anyPartitionTruncated — ORs the `truncated` flag across every partition
 * result from `load()`'s bounded `Promise.all` (the desk's admissions read is
 * capped; `truncated: true` on any ONE partition means the active view is
 * missing rows). Single derivation, alongside `computeDeskCounts` — the ONE
 * place both the incomplete-list banner and any future truncation-aware
 * surface read from; never a second inline OR in a component.
 *
 * `truncated` is an ADDITIVE, optional response field (see admissionApi
 * `listAdmissions`) — a result missing it entirely (older backend) reads as
 * `false`, never a crash and never a false-positive banner.
 */
export function anyPartitionTruncated(results) {
  const list = Array.isArray(results) ? results : [];
  return list.some((r) => !!(r && r.data && r.data.truncated));
}

/**
 * patchAdmissionBalance — apply a `admission_balance_changed` WS payload to the
 * loaded rows WITHOUT a refetch (S2 AC4: balance moves live). Patches ONLY the
 * matching row's server-owned balance/deposit/dues fields (single source — the
 * row denorm the desk + chip + Payment Pending queue all read); leaves every
 * other row identity-stable so React doesn't re-render the whole list. Unknown
 * admission_id → rows returned as-is (no-op; the row simply isn't in this
 * partition).
 *
 * payload: { admission_id, balance_paise, deposit_total_paise, deposit_low,
 *            deposit_shortfall_paise, dues_paise, dues_status, dues_collected_paise,
 *            planned_discharge_at, discharge_date_confirmed_at }
 * (C6b FE-4 — the WS balance-patch event is additive: dues_paise/dues_status/
 * dues_collected_paise ride the SAME event, emitted post-commit from the
 * written row, per build-plan §5 WS contract. A collect/write-off's own
 * response fields are ALSO fed through this one selector so the desk row, the
 * "With dues" chip and the Payment Pending queue can never disagree.
 * `planned_discharge_at` (S4/FE-3) is forwarded by `AdmissionWorkspace` after
 * a Plan-discharge save/clear so the desk-row chip updates with NO refetch;
 * unlike the money fields above, `null` is a LEGITIMATE cleared-plan value
 * (not "absent"), so this key is patched whenever the payload carries it at
 * all — checked with `in`, never `!= null`.)
 */
export function patchAdmissionBalance(rows, payload) {
  const list = Array.isArray(rows) ? rows : [];
  const id = payload && payload.admission_id;
  if (!id || !list.some((r) => r.admission_id === id)) return list;
  return list.map((r) => {
    if (r.admission_id !== id) return r;
    const next = { ...r };
    // admission-access — a row whose money the server withheld
    // (`money_visible === false`, fields null) never GAINS money from a live
    // patch (the WS event is org-wide): the money fields below are skipped for
    // it, so it can never render a ₹0 / balance it was not allowed to see.
    // Non-money keys (planned discharge, payer class) still patch.
    if (!isMoneyHidden(r)) {
      if (payload.balance_paise != null) next.balance_paise = payload.balance_paise;
      if (payload.deposit_total_paise != null) next.deposit_total_paise = payload.deposit_total_paise;
      if (payload.deposit_low != null) next.deposit_low = payload.deposit_low;
      if (payload.deposit_shortfall_paise != null) next.deposit_shortfall_paise = payload.deposit_shortfall_paise;
      if (payload.dues_paise != null) next.dues_paise = payload.dues_paise;
      if (payload.dues_status != null) next.dues_status = payload.dues_status;
      if (payload.dues_collected_paise != null) next.dues_collected_paise = payload.dues_collected_paise;
    }
    if ('planned_discharge_at' in payload) next.planned_discharge_at = payload.planned_discharge_at;
    // DOD rule v3 (C4) — the marker that says the date above is a CONFIRMED
    // decision, not a plan. Without it the desk row keeps reading "Planned
    // discharge:" after an initiate until the next refetch (the chip selector
    // reads `dodIsConfirmed`, which reads THIS key). Same `in` test as the
    // date itself — `null` is a legitimate value, not "absent".
    if ('discharge_date_confirmed_at' in payload) next.discharge_date_confirmed_at = payload.discharge_date_confirmed_at;
    // FIX-14 — the payer pair, forwarded by `AdmissionWorkspace` off the
    // insurance/details PATCH response (the server derives one from the
    // other, §C8). Without them the desk row, its export and the rail chip
    // keep the pre-save payer class until the next refetch. Both are always
    // set on a saved row, so the money fields' `!= null` test applies — a
    // response that omits one leaves that key alone rather than blanking it.
    if (payload.payer_type != null) next.payer_type = payload.payer_type;
    if (payload.payer_mode != null) next.payer_mode = payload.payer_mode;
    return next;
  });
}

/**
 * mergeSuggestionRows — loaded rail rows FIRST, then warm-up index rows, deduped
 * by admission_id (rail version wins). Pure/in-memory: the rail is always a
 * valid suggestion source even when the index is empty/partial.
 */
export function mergeSuggestionRows(railRows, indexRows) {
  const rail = Array.isArray(railRows) ? railRows : [];
  const idx = Array.isArray(indexRows) ? indexRows : [];
  const seen = new Set(rail.map((r) => r.admission_id));
  return [...rail, ...idx.filter((r) => !seen.has(r.admission_id))];
}

/**
 * matchSuggestions — name/IP match over the warm-up index, capped at `cap`.
 * Returns { items, more } where `more` is the count beyond the cap (0 when none).
 */
export function matchSuggestions(indexRows, q, cap = 8) {
  const query = (q || '').trim().toLowerCase();
  const list = Array.isArray(indexRows) ? indexRows : [];
  if (!query) return { items: [], more: 0 };
  const hits = list.filter((r) => matchesQuery(r, query));
  return { items: hits.slice(0, cap), more: Math.max(0, hits.length - cap) };
}

// ═══════════════════════════════════════════════════════════════════════
// Payment Pending queue (C6b · FE-3) — pure sort/search/aging/totals over
// the ONE bounded `fetchDuesPending()` array (`DuesPendingRow[]`, contract
// §2). These are ADDITIVE exports only — no existing bucket/status logic
// above is touched. The queue container (`PaymentPendingQueue.jsx`) is the
// only consumer today; kept here (not a component-local helper) so the same
// sort/search/totals selector is available to any future dues surface,
// matching the "single shared selector" convention this file already sets
// for `applyDeskFilters`/`sortDeskRows`/`computeDeskCounts`.
// ═══════════════════════════════════════════════════════════════════════

export const DUES_QUEUE_SORT = {
  OLDEST: 'oldest', // default — copy-deck §1.4 "Oldest first"
  AMOUNT: 'amount', // copy-deck §1.4 "Largest amount first"
};

/**
 * sortDuesPendingRows — "Oldest first" (default, earliest `discharged_at`
 * first — agrees with the server's own `ScanIndexForward=True` order, contract
 * §2) or "Largest amount first" (`dues_paise` descending). Never mutates the
 * input array.
 */
export function sortDuesPendingRows(rows, sort) {
  const list = Array.isArray(rows) ? [...rows] : [];
  if (sort === DUES_QUEUE_SORT.AMOUNT) {
    return list.sort((a, b) => (Number(b.dues_paise) || 0) - (Number(a.dues_paise) || 0));
  }
  return list.sort((a, b) => String(a.discharged_at || '').localeCompare(String(b.discharged_at || '')));
}

/**
 * filterDuesPendingRows — client-side search (name/IP, same match rule as
 * `matchesQuery`) AND-composed with an aging-bucket quick filter (copy-deck
 * §1.4 aging chips — bucket keys come from the ONE shared `duesAgingBucket`,
 * never a second age-range calc). `agingBucket` falsy/'' = no narrowing.
 */
export function filterDuesPendingRows(rows, { q, agingBucket } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  const query = (q || '').trim().toLowerCase();
  return list.filter((r) => {
    if (agingBucket) {
      const days = duesAgingDays(r.discharged_at);
      if (duesAgingBucket(days) !== agingBucket) return false;
    }
    return matchesQuery(r, query);
  });
}

/**
 * duesPendingTotals — the ONE shared selector for the queue's header/chip
 * totals (copy-deck §1.1 "{n} admissions · ₹{X} outstanding"). Derived from
 * the CURRENTLY loaded rows (never the server envelope's static count/sum),
 * so a live `patchRow`/`removeRow` after a collect/write-off keeps the header
 * true WITHOUT a second fetch — single source, no drift between chip and list.
 */
export function duesPendingTotals(rows) {
  const list = Array.isArray(rows) ? rows : [];
  // admission-access — a row with no amount (`dues_paise` null: money withheld)
  // is neither counted nor summed, so the header never shows a fake ₹0.
  return list.reduce(
    (acc, r) => (r.dues_paise == null
      ? acc
      : { count: acc.count + 1, totalPaise: acc.totalPaise + (Number(r.dues_paise) || 0) }),
    { count: 0, totalPaise: 0 },
  );
}
